import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Building2, Plus, TrendingUp, DollarSign, CheckCircle, Heart, AlertCircle } from 'lucide-react'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { marketplaceApi } from '../../services/api/marketplace'
import { DstListing } from '../../services/api/admin'
import { useMarketplaceFavorites } from '../../hooks/useMarketplaceFavorites'
import { useAppPath } from '../../hooks/useAppPath'
import {
  formatCentsCompact,
  formatReturnRange,
  assetClassBadgeClass,
} from '../../lib/marketplaceFormatters'
import { cn } from '../../lib/utils'
import { SponsorMonogram } from '../marketplace/SponsorMonogram'
import { canAddProperty, type IdentificationRule, type PropertyForRules } from '../../lib/identification/ruleEnforcement'

export interface DstPickerSelection {
  address: string
  estimatedFMV: number
  dstListingId: string
  dstTitle: string
}

interface IdentificationDstPickerProps {
  onSelect: (selection: DstPickerSelection) => void
  disabled?: boolean
  rule?: IdentificationRule | ''
  existingProperties?: PropertyForRules[]
  relinquishedFMV?: number | null
  alreadyAddedIds?: string[]
}

function formatUSD(value: number): string {
  return Math.round(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function DstPickerCard({
  listing,
  onAdd,
  disabled,
  ruleBlockReason,
  alreadyAdded,
}: {
  listing: DstListing
  onAdd: (investmentAmount: number) => void
  disabled: boolean
  ruleBlockReason?: string
  alreadyAdded?: boolean
}) {
  const [investmentInput, setInvestmentInput] = useState<string>('')
  const location = [listing.addressCity, listing.addressState].filter(Boolean).join(', ') || '—'
  const fmvDollars = listing.askingPriceCents != null ? listing.askingPriceCents / 100 : 0
  const minInvestmentDollars = listing.dstMinInvestmentCents != null ? listing.dstMinInvestmentCents / 100 : 0
  const ruleBlocked = !!ruleBlockReason
  const investmentValue = parseFloat(investmentInput.replace(/[^0-9.]/g, '')) || 0
  const investmentValid = investmentValue > 0
  const percentOfDst = fmvDollars > 0 && investmentValue > 0 ? (investmentValue / fmvDollars) * 100 : null

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border transition-colors',
        ruleBlocked
          ? 'bg-amber-50/40 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800 opacity-80'
          : 'bg-muted/40 border-border hover:border-blue-300 dark:hover:border-blue-600',
      )}
    >
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
        {listing.images[0] ? (
          <img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <SponsorMonogram sponsor={listing.sponsor} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{listing.title}</p>
            <p className="text-xs text-muted-foreground">{location}</p>
          </div>
          {listing.qualifiedStatus === 'qualified' && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-semibold"
              aria-label="Qualified DST"
            >
              <CheckCircle className="h-2.5 w-2.5" aria-hidden="true" />
              QST
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <TrendingUp className="h-3 w-3" aria-hidden="true" />
            {formatReturnRange(listing.dstExpectedReturnMin, listing.dstExpectedReturnMax)}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <DollarSign className="h-3 w-3" aria-hidden="true" />
            Min {formatCentsCompact(listing.dstMinInvestmentCents)}
          </span>
          {listing.dstAssetClass && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize',
                assetClassBadgeClass(listing.dstAssetClass)
              )}
            >
              {listing.dstAssetClass}
            </span>
          )}
        </div>
        {fmvDollars > 0 && (
          <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1 font-medium">
            Total DST size: ${fmvDollars.toLocaleString('en-US')}
          </p>
        )}
        {ruleBlocked && (
          <p
            className="mt-1.5 flex items-start gap-1 text-[10px] text-amber-800 dark:text-amber-200"
            role="note"
            aria-live="polite"
          >
            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{ruleBlockReason}</span>
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1 items-end flex-shrink-0">
        <Input
          type="number"
          value={investmentInput}
          onChange={(e) => setInvestmentInput(e.target.value)}
          placeholder={minInvestmentDollars > 0 ? `min $${minInvestmentDollars.toLocaleString('en-US')}` : 'Your investment $'}
          disabled={disabled || ruleBlocked}
          aria-label={`Investment amount for ${listing.title}`}
          className="h-7 w-32 text-xs border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
        />
        {percentOfDst != null && (
          <span className="text-[9px] text-muted-foreground">{percentOfDst.toFixed(2)}% of DST</span>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onAdd(investmentValue)}
          disabled={disabled || ruleBlocked || !investmentValid || !!alreadyAdded}
          aria-label={alreadyAdded ? `${listing.title} already added` : `Add ${listing.title} to identification list`}
          title={alreadyAdded ? 'Already added' : ruleBlockReason || (investmentValid ? '' : 'Enter your investment amount first')}
          className="h-7 px-2 text-xs border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
        >
          {alreadyAdded ? (
            <>
              <CheckCircle className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Added
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Add
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

interface RuleCapacityMeterProps {
  rule: IdentificationRule
  existingProperties: PropertyForRules[]
  relinquishedFMV: number | null
}

function RuleCapacityMeter({ rule, existingProperties, relinquishedFMV }: RuleCapacityMeterProps) {
  if (rule === '3-property') {
    const used = existingProperties.length
    const remaining = Math.max(0, 3 - used)
    return (
      <div className="rounded-md bg-blue-100/70 dark:bg-blue-900/20 px-3 py-1.5 text-[11px] text-blue-800 dark:text-blue-200">
        <strong>3-Property Rule:</strong> {used} of 3 used &middot;{' '}
        {remaining === 0 ? 'limit reached — switch to the 200% rule to add more.' : `${remaining} slot${remaining === 1 ? '' : 's'} remaining`}
      </div>
    )
  }
  if (rule === '200-percent' && relinquishedFMV != null && relinquishedFMV > 0) {
    const used = existingProperties.reduce((sum, p) => sum + (p.estimatedFMV || 0), 0)
    const limit = relinquishedFMV * 2
    const remaining = Math.max(0, limit - used)
    const pct = Math.min(100, (used / limit) * 100)
    return (
      <div className="rounded-md bg-blue-100/70 dark:bg-blue-900/20 px-3 py-1.5 text-[11px] text-blue-800 dark:text-blue-200 space-y-1">
        <div>
          <strong>200% Rule:</strong> {formatUSD(used)} of {formatUSD(limit)} cap used &middot;{' '}
          {remaining === 0 ? 'cap reached — switch to the 95% rule to add more.' : `${formatUSD(remaining)} remaining capacity`}
        </div>
        <div className="h-1 rounded-full bg-blue-200/70 dark:bg-blue-900/40 overflow-hidden">
          <div className="h-full bg-blue-600 dark:bg-blue-400" style={{ width: `${pct}%` }} />
        </div>
      </div>
    )
  }
  if (rule === '95-percent') {
    return (
      <div className="rounded-md bg-blue-100/70 dark:bg-blue-900/20 px-3 py-1.5 text-[11px] text-blue-800 dark:text-blue-200">
        <strong>95% Rule:</strong> unlimited identifications, but you must close on at least 95% of the aggregate identified FMV.
      </div>
    )
  }
  return null
}

export function IdentificationDstPicker({
  onSelect,
  disabled = false,
  rule,
  existingProperties = [],
  relinquishedFMV = null,
  alreadyAddedIds = [],
}: IdentificationDstPickerProps) {
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(false)
  const { favorites } = useMarketplaceFavorites()
  const appPath = useAppPath()

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketplace-listings-picker'],
    queryFn: () => marketplaceApi.listListings({ limit: 50 }),
    staleTime: 5 * 60 * 1000,
  })

  const listings = data?.listings ?? []

  const favoritedListings = listings.filter((listing) => favorites.includes(listing.id))

  const filtered = favoritedListings.filter((listing) => {
    if (!search.trim()) return true
    const query = search.toLowerCase()
    return (
      listing.title.toLowerCase().includes(query) ||
      (listing.sponsor ?? '').toLowerCase().includes(query) ||
      (listing.addressCity ?? '').toLowerCase().includes(query) ||
      (listing.addressState ?? '').toLowerCase().includes(query) ||
      (listing.dstAssetClass ?? '').toLowerCase().includes(query)
    )
  })

  function handleAdd(listing: DstListing, investmentAmount: number) {
    const location = [listing.addressCity, listing.addressState].filter(Boolean).join(', ')
    const addressLabel = location
      ? `${listing.title} — ${location}`
      : listing.title
    onSelect({
      address: addressLabel,
      estimatedFMV: investmentAmount,
      dstListingId: listing.id,
      dstTitle: listing.title,
    })
  }

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls="dst-picker-panel"
      >
        <span className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
            Pick from DST Marketplace
          </span>
          {favoritedListings.length > 0 && (
            <span className="rounded-full bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-[10px] font-bold px-1.5 py-0.5">
              {favoritedListings.length}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
          <Heart className="h-3 w-3" aria-hidden="true" />
          {expanded ? 'Hide' : 'Browse saved DSTs'}
        </span>
      </button>

      {expanded && (
        <div id="dst-picker-panel" className="px-4 pb-4 space-y-3">
          {rule && (
            <RuleCapacityMeter
              rule={rule as IdentificationRule}
              existingProperties={existingProperties}
              relinquishedFMV={relinquishedFMV}
            />
          )}
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, sponsor, location, or asset class"
              className="pl-8 h-8 text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
              aria-label="Search DST marketplace listings"
            />
          </div>

          {isPending && (
            <div className="space-y-2" aria-busy="true" aria-label="Loading DST listings">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className="h-16 rounded-lg bg-muted/60 animate-pulse"
                  aria-hidden="true"
                />
              ))}
            </div>
          )}

          {isError && (
            <p className="text-xs text-red-600 dark:text-red-400 py-2" role="alert">
              Failed to load marketplace listings. Please try again.
            </p>
          )}

          {!isPending && !isError && favoritedListings.length === 0 && (
            <div className="py-4 text-center space-y-2">
              <Heart className="h-8 w-8 text-muted-foreground mx-auto" aria-hidden="true" />
              <p className="text-sm font-medium text-foreground">No DSTs saved yet.</p>
              <p className="text-xs text-muted-foreground">
                Browse the Marketplace to add DSTs to your selections.
              </p>
              <Link
                to={appPath('/marketplace')}
                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-200"
              >
                Go to Marketplace
              </Link>
            </div>
          )}

          {!isPending && !isError && favoritedListings.length > 0 && filtered.length === 0 && (
            <p className="text-xs text-muted-foreground py-3 text-center">
              No DSTs match your search.
            </p>
          )}

          {!isPending && !isError && filtered.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-0.5" role="list" aria-label="DST marketplace listings">
              {filtered.map((listing) => {
                const ruleCheck = rule
                  ? canAddProperty(
                      rule as IdentificationRule,
                      existingProperties,
                      0,
                      relinquishedFMV,
                    )
                  : { canAdd: true, valid: true }
                return (
                  <div key={listing.id} role="listitem">
                    <DstPickerCard
                      listing={listing}
                      onAdd={(investmentAmount) => handleAdd(listing, investmentAmount)}
                      disabled={disabled}
                      ruleBlockReason={ruleCheck.canAdd ? undefined : ruleCheck.disabledReason}
                      alreadyAdded={alreadyAddedIds.includes(listing.id)}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
